import React, { useState, useEffect } from 'react';
import api from './api';

// --- HELPER: Date Formatter ---
const formatDate = (dateObj) => {
    if (!dateObj) return '-';
    return new Date(dateObj).toLocaleDateString('en-GB');
};

const CutIssue = () => {
    // View State
    const [tab, setTab] = useState('new'); 

    // Data State
    const [stock, setStock] = useState([]);
    const [cutList, setCutList] = useState([]); 
    const [selectedParent, setSelectedParent] = useState(null);
    const [nextGlobalName, setNextGlobalName] = useState('A'); 
    
    // Form State
    const [cuts, setCuts] = useState([]);

    useEffect(() => {
        loadStock();
        loadCutHistory();
        fetchNextName(); 
    }, []);

    const loadStock = async () => {
        try {
            const res = await api.get('/rough');
            setStock(res.data.data.filter(l => l.status === 'Rough'));
        } catch (err) { console.error("Error loading stock"); }
    };

    const loadCutHistory = async () => {
        try {
            const res = await api.get('/cuts'); 
            setCutList(res.data);
        } catch (err) { console.error("Error loading history"); }
    };

    const fetchNextName = async () => {
        try {
            const res = await api.get('/cuts/next-name');
            setNextGlobalName(res.data.nextName || 'A');
        } catch (err) { console.error("Error fetching next name"); }
    };

    // --- FORM HANDLERS ---
    const handleParentSelect = async (e) => {
        const lotId = e.target.value;
        const lot = stock.find(l => l._id === lotId);
        setSelectedParent(lot || null);

        let startName = 'A';
        if (lotId) {
            try {
                const res = await api.get('/cuts/next-name');
                if(res.data && res.data.nextName) {
                    startName = res.data.nextName;
                }
            } catch (err) { console.error("Error fetching name"); }
        }

        setCuts([{ 
            cutName: startName, 
            pieces: '', 
            weight: '', 
            issueDate: new Date().toISOString().split('T')[0], 
            estDays: 15 
        }]);
    };

    const handleDeleteCut = async (id, fullId) => {
        if(!window.confirm(`Delete Cut ${fullId}? Parent will return to stock.`)) return;
        try {
            await api.delete(`/cuts/${id}`);
            alert("Deleted");
            loadCutHistory(); 
            loadStock();     
        } catch (err) { alert(err.message); }
    };

    const updateCut = (index, field, value) => {
        const newCuts = [...cuts];
        newCuts[index][field] = value;
        setCuts(newCuts);
    };

    const getNextLocalName = (currentName) => {
        const code = currentName.charCodeAt(0);
        return String.fromCharCode(code + 1);
    };

    const addRow = () => {
        const lastRowName = cuts[cuts.length - 1].cutName;
        const nextChar = getNextLocalName(lastRowName); 
        
        setCuts([...cuts, { 
            cutName: nextChar, 
            pieces: '', 
            weight: '', 
            issueDate: new Date().toISOString().split('T')[0], 
            estDays: 15 
        }]);
    };

    const removeRow = (index) => {
        if (cuts.length > 1) setCuts(cuts.filter((_, i) => i !== index));
    };

    const checkDuplicates = () => {
        const names = cuts.map(c => c.cutName.trim().toUpperCase());
        const uniqueNames = new Set(names);
        if (names.length !== uniqueNames.size) {
            alert("❌ Error: Cut Names must be unique!");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!selectedParent) return alert("Select a Rough Lot first");
        if (!checkDuplicates()) return;

        const totalAllocatedWeight = cuts.reduce((acc, c) => acc + (parseFloat(c.weight) || 0), 0);
        if (totalAllocatedWeight > selectedParent.weight + 0.5) {
            if(!window.confirm(`Weight overflow (${totalAllocatedWeight} > ${selectedParent.weight}). Continue?`)) return;
        }

        // Calculate Cost/Ct and Total Value
        const costRate = selectedParent.costPerCarat || 0;
        
        const processedCuts = cuts.map(c => ({
            ...c,
            costRate: costRate, 
            totalValue: (parseFloat(c.weight) || 0) * costRate 
        }));

        try {
            await api.post('/cuts', { parentLotId: selectedParent._id, cuts: processedCuts });
            alert("✅ Cuts Issued Successfully!");
            window.location.reload();
        } catch (err) { alert("Error: " + err.response?.data?.error); }
    };

    const getCompDate = (issueDate, days) => {
        if(!issueDate || !days) return '-';
        const d = new Date(issueDate);
        d.setDate(d.getDate() + parseInt(days));
        return formatDate(d);
    };

    const weightDiff = selectedParent ? (selectedParent.weight - cuts.reduce((acc, c) => acc + (parseFloat(c.weight) || 0), 0)) : 0;

    return (
        <div className="container mt-4 mb-5">
            {/* TABS */}
            <ul className="nav nav-pills mb-4">
                <li className="nav-item">
                    <button className={`nav-link ${tab==='new' ? 'active fw-bold':''}`} onClick={()=>setTab('new')}>✂️ New Issue</button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${tab==='list' ? 'active fw-bold':''}`} onClick={()=>setTab('list')}>📋 Active Cuts (WIP)</button>
                </li>
            </ul>

            {/* TAB 1: NEW ISSUE FORM */}
            {tab === 'new' && (
                <div>
                    <div className="card shadow-sm mb-4 border-primary">
                        <div className="card-header bg-primary text-white fw-bold">1. Select Rough Stock</div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="small fw-bold">Search / Select Rough Lot</label>
                                    <select className="form-select" onChange={handleParentSelect}>
                                        <option value="">-- Choose Stock --</option>
                                        {stock.map(l => (
                                            <option key={l._id} value={l._id}>{l.rpNumber} | {l.weight} Cts | {l.origin}</option>
                                        ))}
                                    </select>
                                </div>
                                {selectedParent && (
                                    <>
                                        <div className="col-md-2"><label className="small text-muted">Origin</label><input className="form-control bg-light" value={selectedParent.origin} readOnly /></div>
                                        <div className="col-md-2"><label className="small text-muted">Sieve</label><input className="form-control bg-light" value={selectedParent.sieveSize} readOnly /></div>
                                        <div className="col-md-2"><label className="small text-muted">Cost / Ct</label><input className="form-control bg-light fw-bold" value={Math.round(selectedParent.costPerCarat)} readOnly /></div>
                                        <div className="col-md-2"><label className="small text-muted">Total Weight</label><input className="form-control bg-warning fw-bold text-dark" value={selectedParent.weight} readOnly /></div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {selectedParent && (
                        <div className="card shadow-sm">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                                <span>2. Define Cuts</span>
                                <span className={`badge ${Math.abs(weightDiff) < 0.1 ? 'bg-success' : 'bg-warning text-dark'}`}>Balance: {weightDiff.toFixed(2)} Cts</span>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-bordered align-middle mb-0 text-center">
                                    <thead className="table-light small">
                                        <tr><th width="10%">Cut Name</th><th width="10%">Pcs</th><th width="12%">Weight</th><th width="15%">Issue Date</th><th width="10%">Days</th><th width="15%">Comp. Date</th><th width="5%"></th></tr>
                                    </thead>
                                    <tbody>
                                        {cuts.map((cut, idx) => (
                                            <tr key={idx}>
                                                <td><input className="form-control text-center fw-bold bg-white" value={cut.cutName} onChange={e => updateCut(idx, 'cutName', e.target.value.toUpperCase())} placeholder="A, B..." /></td>
                                                <td><input type="number" className="form-control text-center" placeholder="0" value={cut.pieces} onChange={e => updateCut(idx, 'pieces', e.target.value)} /></td>
                                                <td><input type="number" className="form-control text-center fw-bold" placeholder="0.00" value={cut.weight} onChange={e => updateCut(idx, 'weight', e.target.value)} /></td>
                                                <td><input type="date" className="form-control form-control-sm" value={cut.issueDate} onChange={e => updateCut(idx, 'issueDate', e.target.value)} /></td>
                                                <td><input type="number" className="form-control text-center" value={cut.estDays} onChange={e => updateCut(idx, 'estDays', e.target.value)} /></td>
                                                <td className="bg-light text-primary fw-bold small">{getCompDate(cut.issueDate, cut.estDays)}</td>
                                                <td><button className="btn btn-sm btn-outline-danger border-0" onClick={() => removeRow(idx)}>✖</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="card-footer p-3 d-flex justify-content-between">
                                <button className="btn btn-secondary" onClick={addRow}>+ Add Next Cut</button>
                                <button className="btn btn-success px-5 fw-bold" onClick={handleSubmit}>CONFIRM ISSUE</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: ACTIVE CUTS LIST */}
            {tab === 'list' && (
                <div className="card shadow-sm">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 text-center">
                            <thead className="table-dark">
                                <tr>
                                    <th>Date</th>
                                    <th>Cut ID</th>
                                    <th>Parent Source</th>
                                    <th>Pieces</th>
                                    <th>Weight</th>
                                    <th>Total Value</th>
                                    <th className="bg-primary text-white">Cost / Ct</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cutList.length === 0 ? <tr><td colSpan="10" className="p-4 text-muted">No cuts found.</td></tr> : 
                                cutList.map(c => (
                                    <tr key={c._id}>
                                        <td>{formatDate(c.issueDate)}</td>
                                        <td className="fw-bold text-primary">{c.cutName}</td>
                                        <td className="text-start">
                                            <div className="small fw-bold">{c.parentRpNumber}</div>
                                            <div className="small text-muted">{c.origin} ({c.sieveSize})</div>
                                        </td>
                                        <td>{c.pieces}</td>
                                        <td className="fw-bold">{c.weight}</td>
                                        
                                        {/* CHANGED: Replaced $ with ₹ */}
                                        <td>₹{(c.totalValue || 0).toLocaleString()}</td>
                                        <td className="fw-bold text-primary bg-light">₹{(c.costRate || 0).toFixed(2)}</td>

                                        <td>
                                            <span className={`badge ${c.status === 'Completed' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                {c.status}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-outline-danger border-0" 
                                                title="Delete Cut"
                                                onClick={() => handleDeleteCut(c._id, c.fullCutId)}
                                            >
                                                ✖
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CutIssue;